import React, { useState, useRef } from "react";
import { api, coverUrl, fmtDuration } from "@/lib/api";
import { toast } from "sonner";
import { Music, Trash2, Check, Upload, Pencil, ChevronDown, ChevronUp, GripVertical, ArrowUp, ArrowDown } from "lucide-react";
import { Reorder, useDragControls } from "framer-motion";
import { TagsInput, tagsToForm } from "@/components/TagsInput";
import { PriceToggle } from "@/components/PriceToggle";
import { HighlightSelect, sectionsToForm } from "@/components/HighlightSelect";
import { splitGenres } from "@/components/GenrePill";

const inputCls = "bg-[#16161D] border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs";

export function PackTrackRow({ pack, track: t, index, total, onUpdated, onRemove, onDragEnd, selected, onToggle, onMove }) {
  const controls = useDragControls();
  const [meta, setMeta] = useState({ title: t.title, artist: t.artist || "", bpm: t.bpm || "", genre: t.genre || "", tags: t.tags || [], preview_start: t.preview_start || 0, track_price: t.track_price ?? "" });
  const [open, setOpen] = useState(false);
  const [pub, setPub] = useState({ category: t.category || (/mashup/i.test(t.title) ? "mashup" : /remix/i.test(t.title) ? "remix" : "pool"), price: t.track_price != null ? String(t.track_price) : "0.99", genre: t.genre || "", is_featured: !!t.is_featured, is_top: !!t.is_top, is_highlighted: !!t.is_highlighted, is_new: !!t.is_new, highlight_genres: t.highlight_genres || [], sections: t.sections || [] });
  const [cover, setCover] = useState(null);
  const [busy, setBusy] = useState(false);

  const dirty = meta.title !== t.title || meta.artist !== (t.artist || "") || meta.genre !== (t.genre || "") || Number(meta.preview_start) !== Number(t.preview_start || 0) || String(meta.bpm) !== String(t.bpm || "") || tagsToForm(meta.tags) !== tagsToForm(t.tags) || String(meta.track_price) !== String(t.track_price ?? "");

  const saveMeta = async () => {
    setBusy(true);
    try {
      const res = await api.put(`/admin/packs/${pack.id}/tracks/${t.id}`, {
        title: meta.title, artist: meta.artist || null, genre: meta.genre, preview_start: Number(meta.preview_start) || 0, bpm: meta.bpm ? Number(meta.bpm) : null, tags: meta.tags.map((x) => x.trim()).filter(Boolean),
        track_price: meta.track_price === "" ? null : Number(meta.track_price) || 0,
      });
      onUpdated(res.data);
      toast.success("Pista actualizada");
    } catch {
      toast.error("Error al guardar la pista");
    } finally { setBusy(false); }
  };

  const autoTimer = useRef(null);
  const updatePub = (patch) => {
    const next = { ...pub, ...patch };
    setPub(next);
    if (t.track_id) {
      clearTimeout(autoTimer.current);
      autoTimer.current = setTimeout(() => publish(next, true), 600);
    }
  };

  const publish = async (overrides = {}, keepOpen = false) => {
    const p = { ...pub, ...overrides };
    if (Object.keys(overrides).length) setPub(p);
    setBusy(true);
    const fd = new FormData();
    fd.append("title", meta.title);
    if (meta.artist) fd.append("artist", meta.artist);
    fd.append("category", p.category);
    fd.append("price", p.price || "0");
    fd.append("genre", p.genre);
    fd.append("is_featured", p.is_featured ? "true" : "false");
    fd.append("is_top", p.is_top ? "true" : "false");
    fd.append("is_highlighted", p.is_highlighted ? "true" : "false");
    fd.append("is_new", p.is_new ? "true" : "false");
    fd.append("sections", sectionsToForm(p.sections));
    fd.append("highlight_genres", sectionsToForm(p.highlight_genres));
    if (meta.bpm) fd.append("bpm", meta.bpm);
    fd.append("tags", tagsToForm(meta.tags));
    if (cover) fd.append("cover", cover);
    try {
      const res = await api.post(`/admin/packs/${pack.id}/tracks/${t.id}/publish`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUpdated(res.data);
      if (keepOpen) {
        toast.success("Guardado", { duration: 1200 });
      } else {
        setOpen(false); setCover(null);
        toast.success(`Pista publicada en ${{ remix: "Remixes", mashup: "Mashups", original: "Originales", pool: "Pool" }[p.category]}`);
      }
    } catch {
      toast.error("Error al publicar la pista");
    } finally { setBusy(false); }
  };

  const addVersions = async (files) => {
    if (!files.length) return;
    setBusy(true);
    const fd = new FormData();
    files.forEach((f) => fd.append("versions", f));
    fd.append("version_meta", JSON.stringify(files.map((f) => { const base = f.name.replace(/\.[^.]+$/, ""); const m = /^(.+?)\s+[-–]\s+(.+)$/.exec(base); return { title: m ? m[2].trim() : base, artist: m ? m[1].trim() : meta.artist }; })));
    try {
      const res = await api.post(`/admin/packs/${pack.id}/tracks/${t.id}/versions`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUpdated(res.data); toast.success("Versión añadida");
    } catch { toast.error("Error al añadir la versión"); } finally { setBusy(false); }
  };
  const removeVersion = async (vid) => {
    setBusy(true);
    try {
      const res = await api.delete(`/admin/packs/${pack.id}/tracks/${t.id}/versions/${vid}`);
      onUpdated(res.data);
    } catch { toast.error("No se pudo quitar la versión"); } finally { setBusy(false); }
  };

  const unpublish = async () => {
    setBusy(true);
    try {
      const res = await api.delete(`/admin/tracks/${t.track_id}/unpublish`);
      onUpdated(res.data.pack);
      setOpen(false);
      toast.success("Pista quitada de la sección");
    } catch {
      toast.error("No se pudo quitar de la sección");
    } finally { setBusy(false); }
  };

  return (
    <Reorder.Item value={t} dragListener={false} dragControls={controls} onDragEnd={onDragEnd}
      className="bg-black/30 rounded-lg p-2 space-y-2 select-none" data-testid={`edit-pack-track-${t.id}`}>
      <div className="flex items-center gap-2">
        <input type="checkbox" checked={!!selected} onChange={onToggle} data-testid={`pack-track-select-${t.id}`} className="accent-red-600" />
        <div className="flex flex-col">
          <button type="button" onClick={() => onMove(-1)} disabled={index === 0} data-testid={`pack-track-up-${t.id}`}
            className="text-white/40 hover:text-white disabled:opacity-20 p-0.5"><ArrowUp className="w-3.5 h-3.5" /></button>
          <button type="button" onClick={() => onMove(1)} disabled={index === total - 1} data-testid={`pack-track-down-${t.id}`}
            className="text-white/40 hover:text-white disabled:opacity-20 p-0.5"><ArrowDown className="w-3.5 h-3.5" /></button>
        </div>
        <button type="button" onPointerDown={(e) => controls.start(e)} data-testid={`pack-track-drag-${t.id}`}
          className="text-white/30 hover:text-white cursor-grab active:cursor-grabbing touch-none p-0.5"><GripVertical className="w-4 h-4" /></button>
        <input type="number" min="1" max={total} defaultValue={index + 1} key={`pos-${index}`} data-testid={`pack-track-position-${t.id}`}
          onBlur={(e) => { const n = parseInt(e.target.value, 10); if (n >= 1 && n <= total && n !== index + 1) onMove(n - 1 - index); }}
          onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
          title="Número de pista (escribe y pulsa Enter)"
          className="w-11 bg-[#16161D] border border-white/10 rounded px-1 py-0.5 text-white font-mono text-[10px] text-center" />
        <div className="w-8 h-8 rounded overflow-hidden bg-neutral-900 shrink-0 flex items-center justify-center">
          {t.cover_path ? <img src={coverUrl(t.cover_path, 160)} alt="" className="w-full h-full object-cover" /> : <Music className="w-3.5 h-3.5 text-red-500" />}
        </div>
        <input className={`${inputCls} flex-1 min-w-0`} value={meta.title} placeholder="Título" title={meta.title} data-testid={`pack-track-title-input-${t.id}`}
          onChange={(e) => setMeta({ ...meta, title: e.target.value })} />
        {dirty && (
          <button type="button" onClick={saveMeta} disabled={busy} data-testid={`pack-track-save-${t.id}`}
            className="text-emerald-400 hover:bg-emerald-500/10 p-1.5 rounded flex items-center gap-1 text-[10px] font-semibold" title="Guardar"><Check className="w-4 h-4" /> Guardar</button>
        )}
      </div>
      <div className="ml-10 pr-2 flex items-center gap-2 flex-wrap">
        <input className={`${inputCls} w-40`} value={meta.artist} placeholder="Artista" data-testid={`pack-track-artist-input-${t.id}`}
          onChange={(e) => setMeta({ ...meta, artist: e.target.value })} />
        <input className={`${inputCls} w-16`} type="number" placeholder="BPM" value={meta.bpm} data-testid={`pack-track-bpm-input-${t.id}`}
          onChange={(e) => setMeta({ ...meta, bpm: e.target.value })} />
        <input className={`${inputCls} w-40`} placeholder="Género(s), coma" title="Varios géneros separados por coma" value={meta.genre} data-testid={`pack-track-genre-input-${t.id}`}
          onChange={(e) => setMeta({ ...meta, genre: e.target.value })} />
        <span className="font-mono text-[10px] text-white/40 w-10" data-testid={`pack-track-duration-${t.id}`}>{fmtDuration(t.duration)}</span>
        <label className="flex items-center gap-1 font-mono text-[10px] text-white/50" title="Precio de esta pista comprada suelta (vacío = 0,99 €; 0 = gratis)">
          €<input className={`${inputCls} w-16`} type="number" step="0.01" min="0" placeholder="0.99" value={meta.track_price} data-testid={`pack-track-price-input-${t.id}`}
            onChange={(e) => setMeta({ ...meta, track_price: e.target.value })} />
        </label>
        <span className="flex-1" />
        <label className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-white/60 cursor-pointer" title="Publicar como pista individual en su sección (Remix/Mashup según el título, resto Pool)">
          <input type="checkbox" checked={!!t.track_id} disabled={busy} data-testid={`pack-track-individual-${t.id}`} className="accent-red-600"
            onChange={(e) => (e.target.checked ? publish() : unpublish())} />
          Sección
        </label>
        <label className={`flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest cursor-pointer ${t.track_id ? "text-white/60" : "text-white/25"}`} title="Mostrar en grande (destacada) dentro de su sección">
          <input type="checkbox" checked={!!t.is_highlighted} disabled={busy || !t.track_id} data-testid={`pack-track-highlight-${t.id}`} className="accent-red-600"
            onChange={(e) => publish({ is_highlighted: e.target.checked })} />
          Destacar
        </label>
        <label className={`flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest cursor-pointer ${t.track_id ? "text-white/60" : "text-white/25"}`} title="Mostrar en la página principal (Nuevos drops destacados)">
          <input type="checkbox" checked={!!t.is_featured} disabled={busy || !t.track_id} data-testid={`pack-track-home-${t.id}`} className="accent-red-600"
            onChange={(e) => publish({ is_featured: e.target.checked })} />
          Home
        </label>
        <button type="button" onClick={() => setOpen(!open)} data-testid={`pack-track-publish-toggle-${t.id}`}
          className={`p-1.5 rounded flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest ${t.track_id ? "text-red-400" : "text-white/50 hover:text-white"}`}
          title={t.track_id ? "Publicada en sección — editar" : "Publicar en Remixes/Mashups"}>
          {t.track_id ? <Pencil className="w-3.5 h-3.5" /> : <Upload className="w-3.5 h-3.5" />}
          <span>{t.track_id ? t.category || "publicada" : "Más opciones"}</span>
          {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
        <button type="button" onClick={() => setMeta({ ...meta, bpm: "", tags: [], preview_start: 0 })} data-testid={`pack-track-reset-${t.id}`}
          className="text-[10px] text-white/40 hover:text-red-400 px-1.5" title="Restablecer BPM y tags">Restablecer</button>
        <button type="button" onClick={() => onRemove(t.id)} data-testid={`edit-pack-remove-track-${t.id}`}
          className="text-white/40 hover:text-red-500 p-1.5"><Trash2 className="w-3.5 h-3.5" /></button>
      </div>
      <div className="ml-10 pr-2 space-y-2">
        <TagsInput small value={meta.tags} onChange={(tags) => setMeta({ ...meta, tags })} testid={`pack-track-tag-${t.id}`} />
        <div className="flex items-center gap-2 flex-wrap" data-testid={`pack-track-versions-${t.id}`}>
          <span className="font-mono text-[9px] uppercase tracking-widest text-white/40">Versiones ({(t.versions || []).length})</span>
          {(t.versions || []).map((v) => (
            <span key={v.id} className="inline-flex items-center gap-1 text-[10px] text-white/70 border border-white/10 rounded-full pl-2 pr-1 py-0.5" data-testid={`pack-track-version-${v.id}`}>
              {v.title}
              <button type="button" onClick={() => removeVersion(v.id)} disabled={busy} data-testid={`pack-track-version-remove-${v.id}`} className="text-white/40 hover:text-red-500 px-1">✕</button>
            </span>
          ))}
          <label className={`text-[10px] font-semibold px-2 py-0.5 rounded-full cursor-pointer border border-dashed ${busy ? "opacity-50" : "border-red-800/50 text-red-400 hover:bg-red-600/10"}`} data-testid={`pack-track-add-versions-${t.id}`}>
            + Añadir versión
            <input type="file" multiple accept="audio/*" className="hidden" disabled={busy} data-testid={`pack-track-add-versions-input-${t.id}`}
              onChange={(e) => { addVersions(Array.from(e.target.files)); e.target.value = ""; }} />
          </label>
        </div>
      </div>

      {open && (
        <div className="ml-6 p-3 rounded-lg border border-red-900/40 bg-red-950/10 space-y-2" data-testid={`pack-track-publish-form-${t.id}`}>
          <div className="text-[10px] font-mono uppercase tracking-widest text-red-400">
            {t.track_id ? "Actualizar tema publicado" : "Publicar como tema individual"}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <select className={inputCls} value={pub.category} data-testid={`pack-track-publish-category-${t.id}`}
              onChange={(e) => updatePub({ category: e.target.value })}>
              <option value="remix">Remix</option>
              <option value="mashup">Mashup</option>
              <option value="original">Original</option>
          <option value="pool">Pool</option>
            </select>
            <input className={inputCls} placeholder="Género(s), separados por coma" value={pub.genre} onChange={(e) => setPub({ ...pub, genre: e.target.value })} onBlur={() => t.track_id && publish({}, true)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); t.track_id && publish({}, true); } }} />
          </div>
          <HighlightSelect small value={{ is_featured: pub.is_featured, is_top: pub.is_top, is_highlighted: pub.is_highlighted, is_new: pub.is_new, highlight_genres: pub.highlight_genres, sections: pub.sections }} genres={splitGenres(pub.genre || meta.genre)} category={pub.category} onChange={(v) => updatePub(v)} testid={`pack-track-publish-highlight-${t.id}`} />
          <PriceToggle small value={pub.price} onChange={(price) => updatePub({ price })} testid={`pack-track-publish-price-${t.id}`} />
          <label className="block">
            <span className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1 block">Portada propia (opcional)</span>
            <input type="file" accept="image/*" data-testid={`pack-track-publish-cover-${t.id}`}
              onChange={(e) => setCover(e.target.files[0] || null)}
              className="w-full text-xs text-white/60 file:mr-3 file:py-1 file:px-2 file:rounded file:border-0 file:bg-red-600/20 file:text-red-400 file:font-semibold" />
            {cover && <span className="text-xs text-emerald-400 mt-1 block">{cover.name}</span>}
          </label>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => publish()} disabled={busy} data-testid={`pack-track-publish-submit-${t.id}`}
              className="btn-red text-white text-xs px-4 py-2 rounded-full font-semibold disabled:opacity-50">
              {busy ? "Publicando..." : t.track_id ? "Actualizar en sección" : "Publicar en sección"}
            </button>
            {t.track_id && (
              <button type="button" onClick={unpublish} disabled={busy} data-testid={`pack-track-unpublish-${t.id}`}
                className="text-xs px-4 py-2 rounded-full font-semibold border border-white/10 text-white/60 hover:text-red-400 hover:border-red-800/50 disabled:opacity-50">
                Quitar de la sección
              </button>
            )}
          </div>
        </div>
      )}
    </Reorder.Item>
  );
}
